/**
 * Import owners or tenants from a CSV.
 *
 * Parses entirely client-side (see lib/csv.ts) and, once mapped, submits each row
 * through the same `POST /api/owners` / `POST /api/tenants` the single-record forms
 * use — there is no separate bulk-import endpoint, and there does not need to be one
 * (invariant 1 still holds per request: each row runs through `req.db` under the
 * signed-in admin's own actor context, and each creation is audited exactly like an
 * owner or tenant typed in by hand). Rows are sent one at a time, in order, so a
 * failure partway through is visible against the row that caused it rather than
 * losing the rest of the batch.
 */

import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../../../lib/api.js';
import { parseCsv, type ParsedCsv } from '../../../lib/csv.js';
import { DataTable, type Column } from '../../../components/DataTable.js';

type EntityType = 'owners' | 'tenants';

interface TargetField {
  key: string;
  label: string;
  required: boolean;
}

const OWNER_FIELDS: TargetField[] = [
  { key: 'displayName', label: 'Name', required: true },
  { key: 'email', label: 'Email', required: true },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'line1', label: 'Mailing street', required: false },
  { key: 'city', label: 'City', required: false },
  { key: 'postalCode', label: 'ZIP', required: false },
  { key: 'notes', label: 'Notes', required: false },
];

const TENANT_FIELDS: TargetField[] = [
  { key: 'firstName', label: 'First name', required: true },
  { key: 'lastName', label: 'Last name', required: true },
  { key: 'email', label: 'Email', required: true },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'emergencyContactName', label: 'Emergency contact', required: false },
  { key: 'emergencyContactPhone', label: 'Emergency contact phone', required: false },
];

function fieldsFor(entityType: EntityType): TargetField[] {
  return entityType === 'owners' ? OWNER_FIELDS : TENANT_FIELDS;
}

/** Guess a mapping from CSV headers to target fields by loose name match. */
function autoMap(headers: string[], fields: TargetField[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

  for (const field of fields) {
    const wanted = normalize(field.label);
    const wantedKey = normalize(field.key);
    const match = headers.find((header) => {
      const normalized = normalize(header);
      return normalized === wanted || normalized === wantedKey;
    });
    mapping[field.key] = match ?? '';
  }
  return mapping;
}

function mapRow(
  headers: string[],
  row: string[],
  mapping: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [targetKey, header] of Object.entries(mapping)) {
    if (!header) continue;
    const index = headers.indexOf(header);
    result[targetKey] = index >= 0 ? (row[index] ?? '').trim() : '';
  }
  return result;
}

function rowIsValid(mapped: Record<string, string>, fields: TargetField[]): boolean {
  return fields.every((field) => !field.required || mapped[field.key]);
}

function buildPayload(
  entityType: EntityType,
  mapped: Record<string, string>,
): Record<string, unknown> {
  if (entityType === 'owners') {
    const hasAddress = Boolean(mapped.line1 && mapped.city && mapped.postalCode);
    return {
      displayName: mapped.displayName,
      email: mapped.email,
      phone: mapped.phone || null,
      mailingAddress: hasAddress
        ? { line1: mapped.line1, line2: null, city: mapped.city, postalCode: mapped.postalCode }
        : null,
      notes: mapped.notes || null,
    };
  }

  return {
    firstName: mapped.firstName,
    lastName: mapped.lastName,
    email: mapped.email,
    phone: mapped.phone || null,
    emergencyContactName: mapped.emergencyContactName || null,
    emergencyContactPhone: mapped.emergencyContactPhone || null,
  };
}

type RowStatus = 'pending' | 'creating' | 'created' | 'failed' | 'skipped';

interface RowResult {
  index: number;
  mapped: Record<string, string>;
  status: RowStatus;
  // `| undefined` spelled out for exactOptionalPropertyTypes: the skipped/valid
  // branch below assigns `undefined` explicitly rather than omitting the key.
  message?: string | undefined;
}

export function ImportCsv() {
  const [entityType, setEntityType] = useState<EntityType>('owners');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [results, setResults] = useState<RowResult[] | null>(null);
  const [importing, setImporting] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fields = fieldsFor(entityType);

  function reset() {
    setParsed(null);
    setMapping({});
    setResults(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function chooseEntityType(next: EntityType) {
    if (next === entityType) return;
    setEntityType(next);
    reset();
  }

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileError(null);
    setResults(null);

    try {
      const text = await file.text();
      const csv = parseCsv(text);
      if (csv.headers.length === 0 || csv.rows.length === 0) {
        setFileError('That file has no rows to import.');
        setParsed(null);
        return;
      }
      setParsed(csv);
      setMapping(autoMap(csv.headers, fields));
    } catch {
      setFileError('Could not read that file as CSV.');
      setParsed(null);
    }
  }

  const mappedRows =
    parsed?.rows.map((row, index) => ({
      index,
      mapped: mapRow(parsed.headers, row, mapping),
    })) ?? [];

  const validCount = mappedRows.filter((row) => rowIsValid(row.mapped, fields)).length;

  async function runImport() {
    if (!parsed) return;
    setImporting(true);

    const initial: RowResult[] = mappedRows.map(({ index, mapped }) => ({
      index,
      mapped,
      status: rowIsValid(mapped, fields) ? 'pending' : 'skipped',
      message: rowIsValid(mapped, fields)
        ? undefined
        : `Missing ${fields
            .filter((field) => field.required && !mapped[field.key])
            .map((field) => field.label)
            .join(', ')}`,
    }));
    setResults(initial);

    for (const row of initial) {
      if (row.status === 'skipped') continue;

      setResults(
        (current) =>
          current?.map((r) => (r.index === row.index ? { ...r, status: 'creating' } : r)) ??
          current,
      );

      try {
        await api(`/${entityType}`, {
          method: 'POST',
          body: JSON.stringify(buildPayload(entityType, row.mapped)),
        });
        setResults(
          (current) =>
            current?.map((r) => (r.index === row.index ? { ...r, status: 'created' } : r)) ??
            current,
        );
      } catch (caught) {
        const message = caught instanceof ApiError ? caught.message : 'Something went wrong.';
        setResults(
          (current) =>
            current?.map((r) =>
              r.index === row.index ? { ...r, status: 'failed', message } : r,
            ) ?? current,
        );
      }
    }

    setImporting(false);
  }

  const previewColumns: Column<{ index: number; mapped: Record<string, string> }>[] = [
    { key: 'row', header: '#', render: (r) => r.index + 1 },
    ...fields.map((field) => ({
      key: field.key,
      header: field.label + (field.required ? ' *' : ''),
      render: (r: { mapped: Record<string, string> }) =>
        r.mapped[field.key] || <span className="muted">—</span>,
    })),
  ];

  const resultColumns: Column<RowResult>[] = [
    { key: 'row', header: '#', render: (r) => r.index + 1 },
    {
      key: 'who',
      header: entityType === 'owners' ? 'Name' : 'Name',
      render: (r) =>
        entityType === 'owners'
          ? r.mapped.displayName || '—'
          : `${r.mapped.firstName ?? ''} ${r.mapped.lastName ?? ''}`.trim() || '—',
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        if (r.status === 'created') return <span className="pill">Created</span>;
        if (r.status === 'failed')
          return <span className="pill pill--late">Failed — {r.message}</span>;
        if (r.status === 'skipped')
          return <span className="pill pill--late">Skipped — {r.message}</span>;
        if (r.status === 'creating') return <span className="muted">Creating…</span>;
        return <span className="muted">Pending</span>;
      },
    },
  ];

  const createdCount = results?.filter((r) => r.status === 'created').length ?? 0;
  const failedCount = results?.filter((r) => r.status === 'failed').length ?? 0;
  const doneImporting = results !== null && !importing;

  return (
    <section className="stack">
      <div className="row">
        <h1>Import a CSV</h1>
        <span className="shell__spacer" />
        <Link to="/staff/import" className="button button--quiet">
          ← Onboarding
        </Link>
      </div>

      <div className="card stack">
        <div className="row">
          <strong>What are you importing?</strong>
        </div>
        <div className="row">
          <button
            type="button"
            className={entityType === 'owners' ? 'button' : 'button button--quiet'}
            onClick={() => chooseEntityType('owners')}
            disabled={importing}
          >
            Owners
          </button>
          <button
            type="button"
            className={entityType === 'tenants' ? 'button' : 'button button--quiet'}
            onClick={() => chooseEntityType('tenants')}
            disabled={importing}
          >
            Tenants
          </button>
        </div>

        <label className="field" htmlFor="csvFile">
          <span className="field__label">CSV file</span>
          <input
            ref={fileInputRef}
            id="csvFile"
            name="csvFile"
            className="field__input"
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => void handleFile(event)}
            disabled={importing}
          />
        </label>
        {fileError ? <p className="alert alert--error">{fileError}</p> : null}
      </div>

      {parsed ? (
        <>
          <div className="card stack">
            <strong>Match columns</strong>
            <p className="muted">
              {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'} found. Fields marked{' '}
              <strong>*</strong> are required — a row missing one is skipped rather than sent
              half-filled.
            </p>
            <div className="grid">
              {fields.map((field) => (
                <label key={field.key} className="field">
                  <span className="field__label">
                    {field.label}
                    {field.required ? ' *' : ''}
                  </span>
                  <select
                    className="field__input"
                    value={mapping[field.key] ?? ''}
                    onChange={(event) =>
                      setMapping((current) => ({ ...current, [field.key]: event.target.value }))
                    }
                    disabled={importing}
                  >
                    <option value="">— none —</option>
                    {parsed.headers.map((header) => (
                      <option key={header} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </div>

          <div className="stack">
            <h2>Preview</h2>
            <p className="muted">
              {validCount} of {mappedRows.length} row{mappedRows.length === 1 ? '' : 's'} ready to
              import. Showing the mapped values that will be sent — nothing has been created yet.
            </p>
            <DataTable
              columns={previewColumns}
              rows={mappedRows.slice(0, 20)}
              rowKey={(r) => String(r.index)}
              emptyMessage="Nothing to preview."
            />
            {mappedRows.length > 20 ? (
              <p className="muted">…and {mappedRows.length - 20} more row(s), not shown.</p>
            ) : null}
          </div>

          <div className="row">
            <button
              type="button"
              className="button"
              disabled={importing || validCount === 0}
              onClick={() => void runImport()}
            >
              {importing ? 'Importing…' : `Import ${validCount} ${entityType}`}
            </button>
          </div>

          {results ? (
            <div className="stack">
              <h2>Results</h2>
              {doneImporting ? (
                <p className="muted">
                  {createdCount} created
                  {failedCount > 0 ? `, ${failedCount} failed` : ''}. Each created row is audited
                  the same as one entered by hand.
                </p>
              ) : null}
              <DataTable
                columns={resultColumns}
                rows={results}
                rowKey={(r) => String(r.index)}
                emptyMessage="Nothing imported."
              />
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
